/**
 * First-sync progress: one blocking dialog, one non-blocking strip, one row
 * renderer shared between them.
 *
 * ## What it is for
 *
 * Connecting a mailbox used to drop the user into an inbox that was empty and
 * stayed empty for a while, filling in a page at a time with no indication that
 * anything was happening — "there is a user disconnect on the syncing process
 * and it is bad for user experience". The fix is to say what is happening, with
 * real numbers from the provider, and to hold the inbox until there is enough
 * mail in it to be worth opening.
 *
 * ## Two phases, two components
 *
 * `InitialSyncDialog` covers the inbox while the **window** phase runs — the
 * newest ~1,000 messages. It has no close button and no overlay dismiss,
 * because "let's say they force navigate by typing the URL directly" is the
 * case it exists for; the gate is the job state, so a refresh, a second tab and
 * a pasted URL all land on the same dialog.
 *
 * `InitialSyncStrip` takes over afterwards. The remainder of the mailbox keeps
 * arriving behind the user while they read, which is the compromise that keeps
 * a 30,000-message account from meaning a thirty-minute modal.
 *
 * ## Per provider, because that is how people connect them
 *
 * "Gmail was already synced and later they integrate Outlook" is a stated
 * requirement, so each provider is its own row with its own bar and its own
 * numbers. A single merged bar would show a completed Gmail dragging a
 * just-started Outlook up to 50%.
 */
import React from 'react';
import { AlertCircle, CheckCircle2, DownloadCloud, Loader2, RefreshCw } from 'lucide-react';

import { useStartSync, useSyncProgressLane } from '../../hooks/useSyncProgress';

/** Display names. The API speaks `gmail`/`outlook`; people do not. */
const PROVIDER_LABEL = {
    gmail: 'Gmail',
    outlook: 'Outlook',
};

export function providerLabel(provider) {
    return PROVIDER_LABEL[provider] || provider || 'Mailbox';
}

/**
 * The sentence under a provider's name.
 *
 * `target` is what the bar runs to and changes with the phase; `total` is the
 * mailbox's real size and does not. Both are shown during the blocking phase
 * because "840 / 1,000" alone invites "but I have 32,000 emails" — saying so
 * explicitly is what makes the short wait legible.
 *
 * A null `total` is **unknown**, never zero: the provider would not say, and a
 * denominator invented here would be a number the user could catch us getting
 * wrong. That case renders a count with no denominator.
 */
export function progressLabel(job) {
    const { status, processed = 0, target, total, phase } = job || {};
    if (status === 'error') return 'Sync failed';
    if (status === 'complete') {
        return processed === 1 ? '1 message synced' : `${fmt(processed)} messages synced`;
    }
    if (!target) {
        // Unknown denominator — an honest count and an indeterminate bar.
        return processed ? `${fmt(processed)} messages so far…` : 'Starting…';
    }
    const head = `${fmt(Math.min(processed, target))} / ${fmt(target)}`;
    if (phase === 'window' && total && total > target) {
        return `${head} · ${fmt(total)} in the mailbox, the rest follows in the background`;
    }
    return head;
}

function fmt(n) {
    return Number(n || 0).toLocaleString();
}

/** 0–100, or null when there is no denominator to be a percentage of. */
export function percentOf(job) {
    const { processed = 0, target, status } = job || {};
    if (status === 'complete') return 100;
    if (!target) return null;
    return Math.max(0, Math.min(100, Math.round((processed / target) * 100)));
}

/**
 * The progress bar itself — ONE implementation.
 *
 * `MailboxFilterSelect.MailboxSyncSlider` had its own copy: the same clamp, the
 * same track classes, the same aria spread, the same indeterminate stripe. Two
 * bars that must agree about what "unknown total" looks like, and a fix to the
 * clamp or the stripe would have landed in only one of them.
 *
 * `decorative` is the real difference between the two call sites, and it is a
 * semantic one rather than a style one. Inside the dialog the bar IS the status,
 * so it carries `role="progressbar"`. Inside a mailbox option row it sits within
 * a `<button role="option">` — a children-presentational role — where the
 * progressbar semantics are discarded by assistive tech and its label is
 * silently concatenated into the option's accessible name. There it is
 * `aria-hidden` and the state lives in the row's own text.
 */
export function SyncBar({ pct, tone = 'neutral', height = 'h-1.5', label, decorative = false }) {
    const fill = {
        neutral: 'bg-stone-900',
        accent: 'bg-violet-500',
        done: 'bg-emerald-500',
        failed: 'bg-red-500',
    }[tone] || 'bg-stone-900';

    // `pct === null` means the provider would not say how much mail there is.
    // A bar frozen at 0% reads as broken, so it becomes a moving stripe — and,
    // for a screen reader, an INDETERMINATE progressbar rather than one stuck
    // at zero (no `aria-valuenow` at all).
    const indeterminate = pct === null && tone !== 'failed' && tone !== 'done';

    return (
        <div
            className={`${height} w-full rounded-full bg-stone-100 overflow-hidden`}
            {...(decorative
                ? { 'aria-hidden': 'true' }
                : {
                    role: 'progressbar',
                    'aria-label': label,
                    ...(pct === null
                        ? {}
                        : { 'aria-valuenow': pct, 'aria-valuemin': 0, 'aria-valuemax': 100 }),
                })}
        >
            <div
                className={
                    `h-full rounded-full transition-[width] duration-500 ${fill}`
                    + (indeterminate ? ' animate-pulse w-1/3' : '')
                }
                style={pct === null ? undefined : { width: `${pct}%` }}
                data-testid={label ? `sync-bar-${label}` : undefined}
            />
        </div>
    );
}

/** One provider's row: name, state icon, bar, numbers. */
export function SyncJobRow({ job, onRetry }) {
    const pct = percentOf(job);
    const failed = job.status === 'error';
    const done = job.status === 'complete';

    return (
        <li className="py-3" data-testid={`sync-job-${job.provider}`}>
            <div className="flex items-center gap-2 mb-1.5">
                {failed ? (
                    <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" aria-hidden="true" />
                ) : done ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" aria-hidden="true" />
                ) : (
                    <Loader2 className="w-4 h-4 text-stone-500 animate-spin flex-shrink-0" aria-hidden="true" />
                )}
                <span className="text-sm font-medium text-stone-900">
                    {done ? providerLabel(job.provider) : `Syncing ${providerLabel(job.provider)}`}
                </span>
                {job.mailbox && (
                    <span className="text-xs text-stone-400 truncate min-w-0">{job.mailbox}</span>
                )}
                {failed && onRetry && (
                    <button
                        type="button"
                        onClick={() => onRetry(job.job_id)}
                        className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-stone-600 hover:text-stone-900"
                        data-testid={`sync-retry-${job.provider}`}
                    >
                        <RefreshCw className="w-3 h-3" aria-hidden="true" />
                        Retry
                    </button>
                )}
            </div>

            <SyncBar
                pct={pct}
                tone={failed ? 'failed' : done ? 'done' : 'neutral'}
                label={job.provider}
            />

            <p className="mt-1.5 text-xs text-stone-500" data-testid={`sync-label-${job.provider}`}>
                {failed ? (job.error || 'Sync failed') : progressLabel(job)}
            </p>
        </li>
    );
}

/**
 * The blocking dialog.
 *
 * Deliberately NOT the app's `AlertDialog`: that closes on Escape and on an
 * overlay click, and both are exactly what must not happen here. A fixed
 * overlay with no dismiss path is the honest primitive for "you cannot go in
 * yet" — and the modal is driven by job state, so the only thing that closes it
 * is the sync reaching the point where the inbox is worth opening.
 */
export function InitialSyncDialog({ jobs, onRetry }) {
    const rows = jobs || [];
    const panelRef = React.useRef(null);

    // Focus the panel on mount and keep Tab inside it.
    //
    // Without this the dialog was decorative: `aria-modal` is a promise to
    // assistive technology, not a behaviour, and nothing stopped a keyboard
    // user tabbing straight into the inbox behind the overlay. The mailbox
    // SWITCHER was the worst of them — it renders its menu at `z-50` in this
    // same stacking context, so it painted on top of a gate whose entire job
    // is to stop you changing inbox.
    React.useEffect(() => {
        panelRef.current?.focus();
        const onKeyDown = (event) => {
            if (event.key !== 'Tab') return;
            const focusable = panelRef.current?.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            // Nothing focusable inside (the common case — the dialog has no
            // controls unless a provider failed), so Tab has nowhere legitimate
            // to go and is swallowed rather than escaping to the page beneath.
            if (!focusable || focusable.length === 0) {
                event.preventDefault();
                panelRef.current?.focus();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', onKeyDown, true);
        return () => document.removeEventListener('keydown', onKeyDown, true);
    }, []);

    return (
        <div
            /* `z-[60]`, above this page's own layers. At `z-40` the mailbox
               switcher's `z-50` dropdown painted straight through the gate. */
            className="absolute inset-0 z-[60] flex items-center justify-center bg-white/80 backdrop-blur-sm p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="initial-sync-title"
            data-testid="initial-sync-dialog"
        >
            <div
                ref={panelRef}
                tabIndex={-1}
                className="w-full max-w-md rounded-xl border border-stone-200 bg-white p-6 shadow-xl outline-none"
            >
                <h2
                    id="initial-sync-title"
                    className="text-lg font-semibold text-stone-900"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                    Setting up your inbox
                </h2>
                <p className="mt-1 text-sm text-stone-500">
                    We&rsquo;re bringing your mail across. This only happens once per
                    mailbox &mdash; you can leave this page and come back.
                </p>

                <ul className="mt-4 divide-y divide-stone-100">
                    {rows.map((job) => (
                        <SyncJobRow key={job.job_id} job={job} onRetry={onRetry} />
                    ))}
                </ul>
            </div>
        </div>
    );
}

/**
 * The non-blocking strip, for the remainder phase.
 *
 * Same numbers, no overlay. The user is already reading their mail; this is
 * only here so "where is my older mail" has an answer on screen instead of
 * being something they have to ask about.
 */
export function InitialSyncStrip({ jobs, onRetry }) {
    const rows = (jobs || []).filter(
        (job) => job.status === 'ready' || job.status === 'error'
    );
    if (!rows.length) return null;

    const failed = rows.some((job) => job.status === 'error');
    return (
        <div
            className={
                'flex items-center gap-3 px-4 py-2 border-b text-sm '
                + (failed
                    ? 'bg-red-50 border-red-200 text-red-800'
                    : 'bg-blue-50 border-blue-200 text-blue-800')
            }
            role="status"
            aria-live="polite"
            data-testid="initial-sync-strip"
        >
            {failed ? (
                <AlertCircle className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
            ) : (
                <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin" aria-hidden="true" />
            )}
            <span className="min-w-0 truncate">
                {rows.map((job, i) => (
                    <React.Fragment key={job.job_id}>
                        {i > 0 && ' · '}
                        {job.status === 'error'
                            ? `${providerLabel(job.provider)} sync failed`
                            : `Still importing older ${providerLabel(job.provider)} mail — ${progressLabel(job)}`}
                    </React.Fragment>
                ))}
            </span>
            {failed && onRetry && (
                <button
                    type="button"
                    onClick={() => onRetry(rows.find((j) => j.status === 'error')?.job_id)}
                    className="ml-auto inline-flex items-center gap-1 text-xs font-medium underline"
                    data-testid="initial-sync-strip-retry"
                >
                    <RefreshCw className="w-3 h-3" aria-hidden="true" />
                    Retry
                </button>
            )}
        </div>
    );
}

export default InitialSyncDialog;


/**
 * The page's first-sync chrome: the strip always, the dialog while blocking.
 *
 * A consumer component rather than JSX inside the page, because the page is
 * what PROVIDES the lane (`SyncProgressProvider`) and a component cannot read
 * its own provider. Keeping both pieces here also means the rule "the strip is
 * unconditional, the dialog is gated" lives next to the components that
 * implement it, instead of being restated at the call site.
 */
export function InboxSyncChrome() {
    const { jobs, blocking, retry } = useSyncProgressLane();
    const onRetry = React.useCallback(
        (jobId) => { if (jobId) retry.mutate(jobId); },
        [retry],
    );

    return (
        <>
            {/* Renders nothing unless a walk is still running behind the user. */}
            <InitialSyncStrip jobs={jobs} onRetry={onRetry} />
            {blocking && <InitialSyncDialog jobs={jobs} onRetry={onRetry} />}
        </>
    );
}


/**
 * "Import your older mail" — the control for a connection that predates the
 * job model.
 *
 * ## Why this exists
 *
 * A first-sync job is created only when a credential row is newly INSERTED.
 * That is deliberate and load-bearing: it is what stops this feature
 * re-walking every tenant's provider history the moment it deploys. The cost
 * is that everyone connected BEFORE it shipped has whatever partial history
 * the old five-page browser loop managed, no job row, and no way to ask for
 * the rest.
 *
 * `useStartSync` was written for exactly that and then wired to nothing — the
 * audit found it reachable only from three `jest.mock` factories. This is the
 * control it was for.
 *
 * ## When it shows
 *
 * Only for a connected provider with NO job row of any kind. Once a sync has
 * ever run, the strip and the dialog own the story and this would be a second,
 * contradictory way to talk about the same thing.
 *
 * @param {Array<{id: string, connected: boolean}>} providers
 */
export function SyncBackfillPrompt({ providers = [] }) {
    const { jobs, inboxId } = useSyncProgressLane();
    const start = useStartSync(inboxId);
    const [asked, setAsked] = React.useState(() => new Set());

    const known = new Set((jobs || []).map((job) => job.provider));
    const offerable = providers.filter(
        (p) => p && p.connected && !known.has(p.id) && !asked.has(p.id)
    );
    if (!offerable.length) return null;

    return (
        <div
            className="flex items-center gap-3 px-4 py-2 border-b bg-stone-50 border-stone-200 text-sm text-stone-600"
            data-testid="sync-backfill-prompt"
        >
            <DownloadCloud className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
            <span className="min-w-0">
                Only recent mail was imported when you connected
                {' '}
                {offerable.map((p) => providerLabel(p.id)).join(' and ')}.
            </span>
            {offerable.map((p) => (
                <button
                    key={p.id}
                    type="button"
                    disabled={start.isPending}
                    onClick={() => {
                        // Optimistically stop offering: the job row takes a
                        // moment to appear and a second click would be a second
                        // walk request. `jobs.start` is idempotent server-side,
                        // but the button should not invite the double-press.
                        setAsked((prev) => new Set(prev).add(p.id));
                        start.mutate(p.id);
                    }}
                    className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-stone-900 underline disabled:opacity-50"
                    data-testid={`sync-backfill-${p.id}`}
                >
                    Import older {providerLabel(p.id)} mail
                </button>
            ))}
        </div>
    );
}
